import { useContext, useEffect, useMemo } from 'react';
import { vendor as vendorApi } from '../../api';
import { EntityFormProvider, VendorChangeRequestContext } from '../../contexts';
import { useApi, useEntityForm } from '../../hooks';
import { VendorContactEntity } from '../../models';
import { VendorContactSchema } from '../../schemas';
import { ChangeRequestTrigger } from '../../types';
import { padChangeRequestArray, unpadChangeRequestArray } from '../../utils/changeRequests';
import { EditDrawerChangeRequest } from '../EditDrawerChangeRequest';
import { VendorContactForm } from '../Vendor/Contacts';
import { ChangeRequestDrawerHeader } from './ChangeRequestDrawerHeader';

export const VendorContactChangeRequest = () => {
  const { changeRequest: vendorChangeRequest, isAfter } = useContext(VendorChangeRequestContext);
  const { data, isLoading } = useApi(vendorApi.getContactChangeRequest, null, vendorChangeRequest.id);
  const { call: update } = useApi(vendorApi.approvedOrRejectContactRequest, { successKey: 'common:success.save' });
  const entity = useMemo(() => new VendorContactEntity(), []);
  const paddedData = useMemo(() => padChangeRequestArray(data, 'emailAddresses', 'phoneNumbers'), [data]);

  const { handleSubmit, reset, setValues, isSubmitDisabled, form, handleError } = useEntityForm<VendorContactEntity>(
    paddedData?.entity || entity,
    VendorContactSchema(),
    {
      variant: 'changeRequest',
      readOnly: !isAfter || vendorChangeRequest.isDeleted,
    },
  );

  useEffect(() => {
    if (data) {
      if (isAfter) {
        setValues(!vendorChangeRequest.isDeleted ? paddedData?.changeRequest || entity : entity);
      } else {
        reset();
      }
    }
  }, [data, isAfter, setValues, reset, entity, paddedData?.changeRequest, vendorChangeRequest.isDeleted]);

  const saveDrawer = (e: VendorContactEntity, trigger: ChangeRequestTrigger) => {
    return update(vendorChangeRequest.id, trigger, unpadChangeRequestArray(e, 'emailAddresses', 'phoneNumbers'));
  };

  return (
    <EntityFormProvider {...form} isLoading={isLoading || !data}>
      <EditDrawerChangeRequest
        open={true}
        entity={entity}
        handleSubmit={handleSubmit}
        handleError={handleError}
        isSubmitDisabled={vendorChangeRequest.isDeleted ? false : isSubmitDisabled}
        reset={reset}
        onSave={saveDrawer}
      >
        <ChangeRequestDrawerHeader />
        <VendorContactForm />
      </EditDrawerChangeRequest>
    </EntityFormProvider>
  );
};
